<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4">
      <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-8">
        <h1 class="text-2xl font-black text-slate-800 tracking-tight">Logs do Sistema</h1>
        <p class="text-sm text-slate-500 font-medium mt-1">Auditoria completa de acessos, cadastros e modificações no sistema</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  OPÇÕES DE FILTRAGEM
              </h2>
          </div>
          <div class="p-8">
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                  <Date
                    @dateValue="[(filter.start_date = $event)]"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                  <Date @dateValue="[(filter.end_date = $event)]" />
                </v-col>
                <v-col cols="12" sm="12" md="6">
                   <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                  <v-autocomplete
                    :disabled="userGroup == 1 ? false : true"
                    :items="prefectures"
                    v-model="filter.prefecture_id"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    placeholder="Todas as prefeituras"
                    class="bg-slate-50"
                    clearable
                    @change="[
                        filter.user_id = null,
                        getProviders(filter.prefecture_id)
                    ]"
                    no-data-text="Nenhuma prefeitura encontrada"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Fornecedor</label>
                  <v-autocomplete
                    :items="providers"
                    v-model="filter.provider_id"
                    item-value="provider_id"
                    item-text="provider_name"
                    placeholder="Todos os fornecedores"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    no-data-text="Nenhum fornecedor encontrado"
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Ação (Opcional)</label>
                  <v-select
                    :items="['LOGIN', 'CREATE', 'UPDATE', 'DELETE']"
                    v-model="filter.action"
                    placeholder="Todas as ações"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Usuário</label>
                  <v-autocomplete
                    :items="filteredUsers"
                    item-value="user_id"
                    item-text="user_login"
                    v-model="filter.user_id"
                    placeholder="Todos os usuários"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    no-data-text="Nenhum usuário encontrado"
                    clearable
                  />
                </v-col>
              </v-row>

              <div class="flex justify-end gap-3 mt-8">
                  <button 
                    @click="search"
                    :disabled="loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small>mdi-filter-variant</v-icon>
                      {{ loader ? 'FILTRANDO...' : 'FILTRAR RESULTADOS' }}
                  </button>
              </div>
          </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                  <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                      REGISTROS ENCONTRADOS
                  </h2>
              </div>
          </div>
          
          <v-data-table
            :loading="loader"
            :items="data"
            :headers="headers"
            :items-per-page="15"
            no-data-text="Nenhum log encontrado"
            loading-text="CARREGANDO LOGS..."
            class="elevation-0"
          >
            <template v-slot:item.created_at="{ item }">
              <span class="text-xs font-semibold text-slate-600">{{ convertDateTime(item.created_at) }}</span>
            </template>
            <template v-slot:item.user_login="{ item }">
              <span class="text-xs font-bold text-slate-800 uppercase">{{ item.user_login || 'Desconhecido' }}</span>
            </template>
            <template v-slot:item.action="{ item }">
              <span v-if="item.action === 'LOGIN'" class="text-[10px] font-black px-2 py-1 rounded bg-blue-50 text-blue-600 uppercase tracking-widest">ACESSO</span>
              <span v-else-if="item.action === 'CREATE'" class="text-[10px] font-black px-2 py-1 rounded bg-emerald-50 text-emerald-600 uppercase tracking-widest">CADASTRO</span>
              <span v-else-if="item.action === 'UPDATE'" class="text-[10px] font-black px-2 py-1 rounded bg-orange-50 text-orange-600 uppercase tracking-widest">EDIÇÃO</span>
              <span v-else-if="item.action === 'DELETE'" class="text-[10px] font-black px-2 py-1 rounded bg-red-50 text-red-600 uppercase tracking-widest">EXCLUSÃO</span>
              <span v-else class="text-[10px] font-black px-2 py-1 rounded bg-slate-100 text-slate-600 uppercase tracking-widest">{{ item.action }}</span>
            </template>
            <template v-slot:item.entity="{ item }">
              <div class="flex flex-col py-2">
                <span class="text-[10px] text-slate-500 font-medium uppercase truncate max-w-[200px]" :title="item.entity">
                  {{ item.entity }}
                </span>
                <span v-if="item.entity_id" class="text-[10px] text-slate-400 font-bold uppercase">Req ID: {{ item.entity_id }}</span>
              </div>
            </template>
            <template v-slot:item.context="{ item }">
                <div class="flex flex-col py-2">
                    <span v-if="item.prefecture_name" class="text-[10px] text-slate-600 font-medium truncate max-w-[150px]" :title="item.prefecture_name">Pref: {{ item.prefecture_name }}</span>
                    <span v-if="item.provider_name" class="text-[10px] text-slate-500 font-medium truncate max-w-[150px]" :title="item.provider_name">Forn: {{ item.provider_name }}</span>
                    <span v-if="!item.prefecture_name && !item.provider_name" class="text-[10px] text-slate-400 font-medium italic">Sistema Global</span>
                </div>
            </template>

            <template v-slot:item.details="{ item }">
              <v-dialog v-model="item.dialog" max-width="600px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small color="blue-grey lighten-4" class="text-[10px] text-slate-700 font-bold tracking-widest shadow-none">
                    <v-icon small left>mdi-code-json</v-icon> Ver Detalhes
                  </v-btn>
                </template>
                <v-card class="rounded-xl overflow-hidden shadow-2xl">
                  <div class="px-6 py-4 flex items-center justify-between" style="background-color: #313131;">
                    <div class="flex items-center gap-3">
                      <v-icon color="orange" small>mdi-information-outline</v-icon>
                      <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Detalhes do Log</h2>
                    </div>
                    <v-btn icon dark small @click="item.dialog = false">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div class="p-6 bg-slate-50">
                    <pre class="text-xs p-4 bg-gray-900 text-green-400 rounded-lg overflow-x-auto font-mono max-h-96">{{ parseDetails(item.details) }}</pre>
                  </div>
                </v-card>
              </v-dialog>
            </template>
            
          </v-data-table>
      </section>

      <v-snackbar
        multi-line
        :color="snack.color"
        v-model="snack.show"
        transition="fade-transition"
        top right
      >
        <span class="text-[11px] font-bold uppercase tracking-widest">{{ snack.message }}</span>
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snack.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      </div>
    </div>
  </v-container>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import Date from "@/components/InputDate.vue";

export default {
  components: { Date },
  mixins: [Helpers],
  data() {
    return {
      userGroup: null,
      loader: false,
      data: [],
      providers: [],
      users: [],
      prefectures: [],
      snack: {
        show: false,
        message: "",
        color: "",
      },
      filter: {
        end_date: "",
        start_date: "",
        provider_id: null,
        prefecture_id: null,
        user_id: null,
        action: null,
      },
      headers: [
        { text: "Data e Hora", value: "created_at" },
        { text: "Usuário", value: "user_login" },
        { text: "Ação", value: "action", align: "center" },
        { text: "Módulo / Entidade", value: "entity" },
        { text: "Vínculo (Pref/Forn)", value: "context" },
        { text: "Detalhes (Payload)", value: "details", align: "right", sortable: false },
      ],
    };
  },
  computed: {
    filteredUsers() {
      if (!this.filter.prefecture_id) return this.users;
      return this.users.filter(u => 
        (u.group_id === 2 && u.table_id === this.filter.prefecture_id) || 
        (u.group_id === 1) // Inclui administradores globais
      );
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.loader = true;
      this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
      this.userGroup = this.$store.getters.getUserData.group_id;
      
      try {
        const [prefRes, usersRes] = await Promise.all([
          this.$http.get("/prefectures"),
          this.$http.get("/users")
        ]);
        this.prefectures = prefRes.data;
        this.users = usersRes.data;

        if (this.filter.prefecture_id) {
            this.getProviders(this.filter.prefecture_id);
        } else {
            // Load all providers if admin
            const provRes = await this.$http.get("/providers");
            this.providers = provRes.data;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loader = false;
      }
    },
    async search() {
      this.loader = true;
      try {
        const res = await this.$http.get("/system_logs", { params: this.filter });
        this.data = res.data.map(item => ({...item, dialog: false}));
      } catch (error) {
        this.snack.show = true;
        this.snack.message = "Erro ao buscar logs!";
        this.snack.color = "error";
      } finally {
        this.loader = false;
      }
    },
    getProviders(prefecture_id) {
      if (!prefecture_id) {
          this.$http.get("/providers").then(r => this.providers = r.data);
          return;
      }
      this.$http
        .get(`/providers/prefecture/${prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    parseDetails(details) {
      try {
        const parsed = typeof details === 'string' ? JSON.parse(details) : details;
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return details;
      }
    }
  },
};
</script>

<style scoped>
</style>
